import React from 'react';

import { DateFormat, formatDate } from '@lyearn/core/utils/date/formatDate';
import { useTranslation } from '@lyearn/i18n';
import { SeatFill } from '@lyearn/icons';
import { Box, Stack, Typography } from '@lyearn/ui';
import { Link } from '@/components/Link';
import routes from '@/pages/corporate/routes';
import { EventStatusEnum, RegistrationSettings } from '@/types/schema';

import InformationBox from '../InformationBox';
import useRecurringUpcomingEvents from './useRecurringUpcomingEvents';

export function isEventRegistered({
  status,
  registrationSettings,
  isUserRegistered,
  isUserInstructor,
}: {
  status: EventStatusEnum;
  registrationSettings?: RegistrationSettings | null;
  isUserRegistered: boolean;
  isUserInstructor: boolean;
}): boolean {
  return isUserInstructor
    ? status === EventStatusEnum.Scheduled
    : status === EventStatusEnum.Scheduled && Boolean(registrationSettings) && isUserRegistered;
}

const Card: React.FC<{ event: ReturnType<typeof useRecurringUpcomingEvents>['events'][0] }> =
  React.memo(({ event }) => {
    const { t } = useTranslation();

    const renderAction = () => {
      if (event.status === EventStatusEnum.Completed) {
        return (
          <Box className="flex h-24 items-center justify-center rounded-full bg-bg-tertiary px-12">
            <Typography color="text-text-secondary" variant="label-03">
              {t('Common:Filter.Finished')}
            </Typography>
          </Box>
        );
      }

      if (isEventRegistered(event)) {
        return (
          <Box className="flex h-24 items-center justify-center rounded-full bg-overlay px-12">
            <Typography color="text-text-white" variant="label-03">
              {t('Events:Registered')}
            </Typography>
          </Box>
        );
      }

      return (
        <InformationBox
          Icon={SeatFill}
          color={{
            icon: event.isFillingFast
              ? 'icon-success'
              : event.availableSeatCount === 0
              ? 'icon-warning'
              : 'icon-purple',
            text: 'text-text-primary',
          }}
          content={
            event.isFillingFast
              ? t('Events:FillingFast')
              : event.availableSeatCount === 0
              ? t('Events:NoSeatsLeft')
              : t('Events:SeatsAvailable')
          }
          iconClassName="h-16 w-16 !me-8"
          variant="body-short-02"
        />
      );
    };

    return (
      <Link
        className="flex h-full w-full cursor-pointer flex-col rounded-16 border border-border px-24 py-20 hover:bg-bg-hover"
        to={routes.EventDetails(event.id)}>
        <Typography color="text-text-primary" variant="label-01">
          {formatDate(event.startTime, DateFormat.DayDate)}
        </Typography>
        <Typography className="mt-4" color="text-text-secondary" variant="body-short-02">
          {`${formatDate(event.startTime, DateFormat.Time)} - ${formatDate(
            event.endTime,
            DateFormat.Time,
          )}`}
        </Typography>
        <Stack className="mt-auto">{renderAction()}</Stack>
      </Link>
    );
  });

export default Card;
